import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { Bundesland } from '@chancenwerk/enums/enums.js';
import { RaumRoutes } from '@chancenwerk/routes/raumRoutes.js';
import { PartnerAPI } from '@chancenwerk/shared/master-data-management/partner/api/PartnerAPI.js';
import { PartnerFilter } from '@chancenwerk/shared/master-data-management/partner/api/subscribePartnerByFilter.js';
import type { Partner } from '@chancenwerk/shared/master-data-management/partner/entities/Partner.js';
import {
  subscribeWorkshopTrainingAttendance,
  WorkshopTrainingAttendanceSearchResult,
} from '@chancenwerk/shared/workshops/api/subscribeWorkshopTrainingAttendance.js';
import {
  applyWorkshopFilterCondition,
  WorkshopFilter,
  WorkshopFilterCondition,
} from '@chancenwerk/shared/workshops/entities/WorkshopFilter.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { firstValueFrom, takeUntil } from 'rxjs';

/**
 * @customElement akademie-component-workshop-reporting
 */
@customElement('akademie-component-workshop-reporting')
export class AkademieComponentWorkshopReporting extends ChemistryLitElement {
  @property() workshopID = new RXController(this, '');
  @property() additionalColumns = new RXController<'Workshop'[]>(this, []);

  @state() private readonly _workshopFilter = new RXController(
    this,
    new WorkshopFilter({ workshopID: '', requiresTraining: true }),
  );

  override connectedCallback(): void {
    super.connectedCallback();
    this.workshopID.observable.pipe(takeUntil(this.disconnected)).subscribe(id => {
      this._workshopFilter.value.workshopID = id;
      this._workshopFilter.next(this._workshopFilter.value);
    });
  }

  private _hint() {
    switch (this._workshopFilter.value.condition) {
      case WorkshopFilterCondition.IN_8_WEEKS:
      case WorkshopFilterCondition.TOOK_LONGER_THAN_8_WEEKS:
        return '(Durchführungsdatum)';
      case WorkshopFilterCondition.MORE_THAN_8_WEEKS:
        return '(Stichtag 8 Wochen verstrichen)';
    }
    return '';
  }

  private _detailsFilter() {
    return html`<cr-text bold>Filter</cr-text>
      <cr-dropdown-multiselection
        clearable
        placeholder="Kooperationspartner"
        icon="business"
        .value=${this._workshopFilter.value.partners}
        .renderString=${(partner: Maybe<Partner>) => {
          if (!partner) return '';
          return partner.toString();
        }}
        .search=${(search: string) =>
          firstValueFrom(
            PartnerAPI.subscribeByFilter(
              0,
              20,
              new PartnerFilter({ search }),
            )({ name: 1, kuerzel: 1, address: { zipCity: { city: 1 } } }),
          )}
        @value-picked=${async (e: ValueEvent<Maybe<Partner[]>>) => {
          this._workshopFilter.value.partners = e.detail.value ?? [];
          this._workshopFilter.next(this._workshopFilter.value);
        }}
      >
      </cr-dropdown-multiselection>
      <cr-grid columns="0.7fr 1fr" space="md">
        <cr-dropdown-selection
          placeholder="Bundesland"
          .selectables=${Object.values(Bundesland)}
          .value=${this._workshopFilter.value.bundesland}
          @value-picked=${(e: ValueEvent<Maybe<Bundesland>>) => {
            this._workshopFilter.value.bundesland = e.detail.value;
            this._workshopFilter.next(this._workshopFilter.value);
          }}
          clearable
        >
        </cr-dropdown-selection>
        <cr-dropdown-selection
          .selectables=${Object.values(WorkshopFilterCondition)}
          placeholder="Tage seit Schulung / Filter-Bedingung"
          @value-picked=${(e: ValueEvent<Maybe<WorkshopFilterCondition>>) => {
            if (!e.detail.value) {
              return;
            }

            applyWorkshopFilterCondition(e.detail.value, this._workshopFilter.value);
            this._workshopFilter.next(this._workshopFilter.value);
          }}
          clearable
        >
        </cr-dropdown-selection>
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().plus({ years: 10 }).year}
          placeholder=${`Von ${this._hint()}`}
          click-to-save
          flex
          clearable
          .value=${this._workshopFilter.value.from}
          @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
            this._workshopFilter.value.from = e.detail.value;
            if (e.detail.value) {
              if (!this._workshopFilter.value.till || this._workshopFilter.value.till < e.detail.value) {
                this._workshopFilter.value.till = e.detail.value.endOf('month');
              }
            }
            this._workshopFilter.next(this._workshopFilter.value);
          }}
        ></cr-date-picker-dropdown>
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().plus({ years: 10 }).year}
          placeholder=${`Bis ${this._hint()}`}
          click-to-save
          flex
          clearable
          .value=${this._workshopFilter.value.till}
          @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
            this._workshopFilter.value.till = e.detail.value;
            if (e.detail.value) {
              if (!this._workshopFilter.value.from || this._workshopFilter.value.from > e.detail.value) {
                this._workshopFilter.value.from = e.detail.value.startOf('month');
              }
            }
            this._workshopFilter.next(this._workshopFilter.value);
          }}
        ></cr-date-picker-dropdown>
      </cr-grid>`;
  }

  private _details() {
    const opts = buildChancenDataViewOptions({
      class: WorkshopTrainingAttendanceSearchResult,
      operation: subscribeWorkshopTrainingAttendance,
      view: new View<typeof WorkshopTrainingAttendanceSearchResult>({ filter: this._workshopFilter.value }),
      onlyShowLastTranslation: true,
      customColumns: [
        {
          columnName: '',
          columnWidth: 'max-content',
          customID: 'icon',
          selectionSet: { user: { vorname: 1, nachname: 1 } },
          getFieldMapping: w => html`
            <cr-button
              mode="icon"
              icon="person"
              href="${`${projectConfig.get('RAUM_URL')}${RaumRoutes.editEmployee.substring(1)}/${w.user?._id}`}"
              target="_blank"
            ></cr-button>
          `,
        },
        {
          columnName: 'Name',
          columnWidth: 'max-content',
          customID: 'username',
          selectionSet: { user: { vorname: 1, nachname: 1, email: 1 } },
          getFieldMapping: w => w.user?.toString(),
        },
        ...(this.additionalColumns.value.includes('Workshop')
          ? [{ columnName: 'Workshop', columnWidth: '300px', fieldName: 'workshop.title' }]
          : []),
        {
          columnName: 'Tage bis Durchführung',
          columnWidth: 'max-content',
          customID: 'days',
          selectionSet: { daysBetweenWorkshop: 1, daysSinceTraining: 1 },
          getFieldMapping: w => {
            return w.daysBetweenWorkshop
              ? `${w.daysBetweenWorkshop} (${Math.floor(w.daysBetweenWorkshop / 7)} Wochen)`
              : `seit ${w.daysSinceTraining} Tagen nicht durchgeführt`;
          },
        },
        {
          columnName: '8 Wochen verstrichen',
          columnWidth: 'max-content',
          customID: 'eightWeeks',
          selectionSet: { eightWeeksDate: 1 },
          getFieldMapping: w => {
            return w.daysBetweenWorkshop && w.daysBetweenWorkshop <= 7 * 8
              ? nothing
              : `${w.eightWeeksDate?.toFormat('dd.MM.yyyy')}`;
          },
        },
        {
          columnName: 'Durchführungsdatum',
          columnWidth: 'max-content',
          customID: 'date',
          selectionSet: { workshopID: 1, participation: { date: 1 } },
          getFieldMapping: w => {
            return w.participation?.date?.toLocaleString() ?? 'nicht eingetragen';
          },
        },
        {
          columnName: 'Weitere Durchführungen',
          columnWidth: 'max-content',
          customID: 'additionalDates',
          selectionSet: { allParticipations: { date: 1, state: 1 } },
          getFieldMapping: w => {
            if ((w.allParticipations?.length ?? 0) < 2) {
              return nothing;
            }
            return html`<x-flex space="sm" padding="sm">
              ${w.allParticipations?.slice(1).map(p => html`<x-text>${p.date?.toLocaleString()} (${p.state})</x-text>`)}
            </x-flex>`;
          },
        },
        {
          fieldName: 'participation.attendanceCount',
          columnName: 'Teilnehmende',
          columnWidth: 'max-content',
          selectionSet: { participation: { attendanceCount: 1 } },
        },
        {
          columnName: 'Partner',
          columnWidth: '1fr',
          customID: 'partner',
          selectionSet: { participation: { partner: { name: 1 } } },
          getFieldMapping: w => {
            if (!w.participation?.partner?.name) {
              return nothing;
            }

            return html`
              <cr-flex space="sm" horizontal crossaxis-center>
                <cr-button
                  mode="icon"
                  icon="school"
                  href="${`${projectConfig.get('RAUM_URL')}${RaumRoutes.editPartner.substring(1)}/${
                    w.participation.partner._id
                  }`}"
                  target="_blank"
                ></cr-button>
                <cr-text>${w.participation.partner.name}</cr-text>
              </cr-flex>
            `;
          },
        },
      ],
    } as DataViewOptions<typeof WorkshopTrainingAttendanceSearchResult>);

    return html`
      <cr-text bold>Einträge</cr-text>
      <cr-data-view .options=${opts.dataViewOptions}> </cr-data-view>
    `;
  }

  override render() {
    return html` <cr-flex space="md"> ${this._detailsFilter()} ${this._details()} </cr-flex>`;
  }
}
